import { api } from '../../shared/api/client'
import type {
  ExplicitFunnelSelection,
  FunnelPlan,
} from '../../components/contentFunnelSelection.types'
import type { PipelineMarketplace } from '../../components/createDesignsWizard.logic'

export interface SeoPipelineReadinessContext {
  context_id: string
  category_prefix: string
  design_type: string
  design: Record<string, unknown>
  category: Record<string, unknown>
  product?: Record<string, unknown>
}

export interface SeoPipelineReadinessMissing {
  kind: string
  kind_title: string
  slot: string
  key1: string
  key2?: string
  seller_id: number
  marketplace: PipelineMarketplace
  reason: 'fragment_missing' | 'context_value_missing' | 'template_missing' | 'template_conflict'
  fields: string[]
  category_prefixes: string[]
  design_types: string[]
  affected_contexts: number
}

export interface SeoPipelineReadinessResponse {
  ok: boolean
  missing: SeoPipelineReadinessMissing[]
}

export const designTypesAPI = {
  getDesignTypes: () => api.get('/design-types'),
  getUniqueTypes: () => api.get('/design-types/unique'),
  getDesignTypesByCategory: (categoryId: number) => api.get(`/design-types/category/${categoryId}`),
  createDesignType: (designTypeData: any) => api.post('/design-types', designTypeData),
  updateDesignType: (id: number, designTypeData: any) => api.put(`/design-types/${id}`, designTypeData),
  deleteDesignType: (id: number) => api.delete(`/design-types/${id}`),
}

/**
 * Потолок на один макет — совпадает с DESIGN_UPLOAD_MAX_BYTES в гейтвее
 * (src/routes/design-uploads.routes.ts). Самый тяжёлый PDF боевого каталога —
 * 98 МБ, средний — 25,7 МБ.
 */
export const MAX_DESIGN_UPLOAD_BYTES = 100 * 1024 * 1024

/** Столько же параллельных загрузок, сколько у рендера превью: быстро, но без залпа. */
const UPLOAD_CONCURRENCY = 4

export interface UploadProgress {
  /** Полностью загруженных файлов. */
  files: number
  totalFiles: number
  /** Отправленных байт — растёт непрерывно, в отличие от счётчика файлов. */
  bytes: number
  totalBytes: number
}

/**
 * Один PDF потоком. Тело — сам File, никакого base64 и никакого FormData.
 *
 * Таймаут щедрый намеренно: на канале ~12 Мбит/с (замер с прода) файл в 98 МБ,
 * делящий полосу с тремя соседями по пулу, идёт больше четырёх минут.
 */
export async function uploadDesignSource(
  file: File,
  onBytes?: (uploadedBytes: number) => void,
): Promise<string> {
  const response: any = await api.post('/designs/uploads', file, {
    headers: {
      'Content-Type': 'application/pdf',
      // Имя нужно только для логов гейтвея: путь на диске задаёт upload_id.
      'X-Upload-Filename': encodeURIComponent(file.name),
    },
    timeout: 900000,
    onUploadProgress: onBytes ? (event) => onBytes(event.loaded) : undefined,
  })
  const uploadId = response?.data?.data?.upload_id ?? response?.data?.upload_id
  if (!uploadId) throw new Error(`Сервер не вернул идентификатор загрузки для ${file.name}`)
  return String(uploadId)
}

/**
 * Загружает исходники всех шаблонов и подменяет `pdfFile` на `upload_id`.
 * Пул, а не Promise.all: 25 одновременных потоков по 15 МБ забивают и канал,
 * и память вкладки.
 */
async function uploadTemplateSources(
  templates: any[],
  onProgress?: (progress: UploadProgress) => void,
): Promise<any[]> {
  const results = new Array<any>(templates.length)
  const queue = templates.map((template, index) => ({ template, index }))
  // Байты считаем по каждому файлу отдельно: пул грузит четыре разом, и общая
  // сумма иначе прыгала бы туда-сюда при повторных событиях прогресса.
  const sentBytes = new Array<number>(templates.length).fill(0)
  const totalBytes = templates.reduce(
    (sum, template) => sum + (Number(template?.pdfFile?.size) || 0), 0,
  )
  let uploaded = 0
  const report = () => onProgress?.({
    files: uploaded,
    totalFiles: templates.length,
    bytes: sentBytes.reduce((sum, value) => sum + value, 0),
    totalBytes,
  })
  report()

  const worker = async () => {
    for (;;) {
      const item = queue.shift()
      if (!item) return
      const { template, index } = item
      const file: File = template.pdfFile
      // Мастер грузит файл ещё на шаге добавления — тогда здесь остаётся только
      // подставить готовый id. Ветка с загрузкой нужна вызывающим, которые
      // отдают файлы сразу (и как запасной путь, если загрузка тогда не удалась).
      const uploadId = template.upload_id
        ? String(template.upload_id)
        : await uploadDesignSource(file, (loaded) => {
          sentBytes[index] = loaded
          report()
        })
      const { pdfFile: _dropped, ...rest } = template
      results[index] = {
        ...rest,
        upload_id: uploadId,
        pdfFileName: file?.name || template.pdfFileName || 'file.pdf',
        existsInYandexDisk: false,
      }
      // Ответ приходит после последнего байта, но события прогресса могли
      // оборваться раньше — добиваем до полного размера, чтобы счётчик не
      // застревал на 99%.
      sentBytes[index] = Number(file?.size) || sentBytes[index]
      uploaded += 1
      report()
    }
  }

  // Первая же упавшая загрузка валит весь запуск: создавать тематику из части
  // макетов нельзя, номера внутри неё сквозные.
  await Promise.all(
    Array.from({ length: Math.min(UPLOAD_CONCURRENCY, queue.length) }, () => worker()),
  )
  return results
}

/**
 * Источник создания макетов не из загруженных PDF, а из задания генератора макетов
 * (контракт К3): шаблоны несут `ai_layout_revision_id` вместо `upload_id`.
 */
export interface BulkCreateAiLayoutSource {
  kind: 'ai_layout_job'
  job_id: string
}

export const designsAPI = {
  uploadDesignSource,
  validatePipelineTargets: (
    targets: Array<{ marketplace: PipelineMarketplace; seller_id: number; cabinet?: string }>,
  ) => api.post('/cards/targets/validate', { targets }),
  getDesigns: (params?: any) => api.get('/designs', { params }),
  getDesign: (designCode: number) => api.get(`/designs/${designCode}`),
  createDesign: (designData: any) => api.post('/designs', designData),
  updateDesign: (designCode: number, designData: any) => api.put(`/designs/${designCode}`, designData),
  deleteDesign: (designCode: number) => api.delete(`/designs/${designCode}`),
  massUpdate: (designCodes: number[], updates: any) => api.put('/designs/mass-update', { design_codes: designCodes, updates }),
  massDelete: (designCodes: number[]) => api.post('/designs/mass-delete', { design_codes: designCodes }),
  exportDesigns: (format?: string) => api.get('/designs/export', { params: { format }, responseType: 'blob' }),
  importDesigns: (file: File, options?: any) => {
    const formData = new FormData()
    formData.append('file', file)
    if (options?.addToProducts) formData.append('addToProducts', String(options.addToProducts))
    if (options?.sendToWB) formData.append('sendToWB', String(options.sendToWB))
    if (options?.sellerId) formData.append('sellerId', String(options.sellerId))
    return api.post('/designs/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000, // 5 минут
    })
  },
  getImportStatus: (jobId: string) => api.get(`/designs/import/status/${jobId}`),
  recalculateCompleteness: (designCodes: number[]) => api.post('/designs/recalculate-completeness', { design_codes: designCodes }),
  recalculateAllCompleteness: () => api.post('/designs/recalculate-all-completeness'),
  bulkCreateDesignTemplates: async (
    commonFields: any,
    templates: any[],
    jobId: string,
    pipelineOptions?: {
      mode: 'designs_only' | 'designs_and_products' | 'full_pipeline'
      targets?: Array<{ marketplace: PipelineMarketplace; seller_id: number; cabinet?: string }>
      publication_mode?: 'draft'
      publish_confirmed?: boolean
      funnel_selection_mode?: 'explicit'
      funnel_selections?: ExplicitFunnelSelection[]
    },
    onUploadProgress?: (progress: UploadProgress) => void,
    source?: BulkCreateAiLayoutSource,
  ) => {
    const existsInYandexDisk = commonFields.existsInYandexDisk || false
    const yandexDiskPath = commonFields.yandexDiskPath

    let processedTemplates: any[]

    if (source) {
      // Макеты из генератора: PDF уже лежат на сервере, загружать нечего — только
      // шаблоны с ai_layout_revision_id (без pdfFile и upload_id).
      processedTemplates = templates.map(({ pdfFile: _file, upload_id: _upload, ...rest }) => rest)
    } else if (existsInYandexDisk && yandexDiskPath) {
      processedTemplates = templates.map(template => ({
        ...template,
        existsInYandexDisk: true,
        yandexDiskPath: yandexDiskPath,
        pdfFile: undefined,
        pdfFileName: template.pdfFileName || 'file.pdf',
      }))
    } else {
      // Файлы уходят потоком по одному, а в теле создания остаются только
      // upload_id. Раньше здесь строился base64 всего набора внутри одного
      // JSON: 20 макетов по 15 МБ давали ~400 МБ, что било и в лимит nginx, и
      // в String::kMaxLength (512 МиБ) — причём последний падал бы прямо в
      // браузере, без единого HTTP-запроса.
      processedTemplates = await uploadTemplateSources(templates, onUploadProgress)
    }

    const { existsInYandexDisk: _, yandexDiskPath: __, ...cleanCommonFields } = commonFields

    // Full-pipeline mode performs an exact, potentially multi-call preflight before
    // creating the background job. Use the shared 30-minute HTTP budget instead of
    // a local timeout shorter than one 30-second service RPC.
    return api.post('/designs/bulk-create-templates', {
      commonFields: cleanCommonFields,
      templates: processedTemplates,
      jobId,
      pipeline_options: pipelineOptions || { mode: 'designs_only' },
      ...(source ? { source } : {}),
    })
  },
  planBulkCreateDesignTemplates: async (
    commonFields: any,
    templates: Array<{
      template_key: string
      pdfFileName?: string
      design_type: string
      [key: string]: unknown
    }>,
    targets: Array<{
      marketplace: PipelineMarketplace
      seller_id: number
      cabinet?: string
    }>,
    source?: BulkCreateAiLayoutSource,
  ): Promise<{ data: { data?: FunnelPlan } & FunnelPlan }> =>
    api.post('/designs/bulk-create-templates/plan', {
      commonFields,
      templates: templates.map(({ template_key, pdfFileName, design_type, ...fields }) => ({
        template_key,
        pdfFileName,
        design_type,
        ...fields,
      })),
      targets,
      ...(source ? { source } : {}),
    }),
  checkPipelineSeoReadiness: (input: {
    marketplace: PipelineMarketplace
    seller_id: number
    category_prefixes: string[]
    design_types: string[]
    contexts: SeoPipelineReadinessContext[]
  }): Promise<{ data: { data?: SeoPipelineReadinessResponse } & SeoPipelineReadinessResponse }> =>
    api.post('/seo/fragments/pipeline-readiness', input),
  getFieldValues: (fieldName: string, search?: string) =>
    api.get(`/designs/field-values/${fieldName}`, { params: { search } }),
  /**
   * Миниатюра по идентификатору потоковой загрузки: файл уже на сервере, в теле
   * уходит только UUID. Прежний путь слал сюда весь PDF base64-строкой — 19 МБ
   * на файл, ~50 секунд на офисном канале при таймауте 60, отчего часть превью
   * обрывалась с ERR_ABORTED.
   */
  renderPdfThumbByUpload: (uploadId: string, width = 400) =>
    api.post('/designs/pdf-thumb', { upload_id: uploadId, width }, { timeout: 120000 }),
  /** Legacy: PDF base64-строкой. Остаётся для замены исходника макета. */
  renderPdfThumb: (pdfBase64: string, width = 400) =>
    api.post('/designs/pdf-thumb', { pdf_base64: pdfBase64, width }, { timeout: 120000 }),
  saveFieldValue: (fieldName: string, value: string) =>
    api.post('/designs/field-values', { fieldName, value }),
  getDesignsWithCompleteness: (params?: any) => api.get('/designs/completeness', { params }),
  getDesignCompleteness: (designCode: number) => api.get(`/designs/${designCode}/completeness`),
  getDesignArticles: (params?: {
    quantityFilter?: 1 | 2;
    categories?: string;
    dateFrom?: string;
    dateTo?: string;
    sellerId?: number;
    linkedStatus?: 'linked' | 'not_linked' | 'all';
    search?: string;
    page?: number;
    limit?: number;
  }) => api.get('/designs/articles', { params }),
  exportDesignArticles: (params?: {
    quantityFilter?: 1 | 2;
    categories?: string;
    dateFrom?: string;
    dateTo?: string;
    sellerId?: number;
    linkedStatus?: 'linked' | 'not_linked' | 'all';
    search?: string;
    exportMode?: 'designs_only' | 'full_table';
  }) => api.get('/designs/articles/export', { params, responseType: 'blob' }),
  getWbProductDetails: (nmId: string, sellerId?: number) =>
    api.get(`/designs/articles/wb-product/${nmId}`, { params: sellerId ? { sellerId } : undefined }),
  getOzonProductDetails: (sku: string, sellerId?: number) =>
    api.get(`/designs/articles/ozon-product/${sku}`, { params: sellerId ? { sellerId } : undefined }),

  // --- Google Sheets Export ---
  exportToGoogleSheets: (spreadsheetId: string, sheetName: string, designCodes?: string[], templateId?: number) =>
    api.post('/designs/export-google-sheets', { spreadsheetId, sheetName, designCodes, mode: 'append', templateId }),
  listGoogleSheetsTargets: () =>
    api.get('/designs/google-sheets/targets'),
  addGoogleSheetsTarget: (name: string, spreadsheetId: string, sheetName: string = 'Лист1') =>
    api.post('/designs/google-sheets/targets', { name, spreadsheetId, sheetName }),
  removeGoogleSheetsTarget: (id: number) =>
    api.delete(`/designs/google-sheets/targets/${id}`),

  // --- Export Templates ---
  getExportTemplates: () =>
    api.get('/designs/field-mapping-templates', { params: { type: 'export' } }),
  createExportTemplate: (name: string, mapping: [string, string][], description?: string) =>
    api.post('/designs/field-mapping-templates', { name, mapping, description, templateType: 'export' }),
  updateExportTemplate: (id: number, data: { name?: string; mapping?: [string, string][]; description?: string }) =>
    api.post(`/designs/field-mapping-templates/${id}/update`, data),
  deleteExportTemplate: (id: number) =>
    api.post(`/designs/field-mapping-templates/${id}/delete`),
}
